import { getBearerTokenFromCookies } from "../utils/utils";
/**
 * Fetch reasons from the API
 * @param {string} type - The type parameter for the reasons API
 * @param {string} token - Bearer token for authorization
 * @param {Object} locationData - Location data for headers
 * @returns {Promise<Object>} - API response
 */
export const fetchReasons = async (type = '6', token, locationData = {}) => {
  try {
    const bearerToken = token || getBearerTokenFromCookies();
    const headers = {
      'accept': '*/*',
      'accept-language': 'en-US,en;q=0.9',
      'authorization': `${bearerToken}`,
      'content-type': 'application/json',
      'platform': '3',
      'priority': 'u=1, i',
      'sec-ch-ua': '"Not)A;Brand";v="8", "Chromium";v="138", "Google Chrome";v="138"',
      'sec-ch-ua-mobile': '?0',
      'sec-ch-ua-platform': '"Windows"',
      'sec-fetch-dest': 'empty',
      'sec-fetch-mode': 'cors',
      'sec-fetch-site': 'cross-site',
      'user-agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/138.0.0.0 Safari/537.36'
    };

    // Add location headers if provided
    if (locationData.city) headers['city'] = locationData.city;
    if (locationData.country) headers['country'] = locationData.country;
    if (locationData.ipaddress) headers['ipaddress'] = locationData.ipaddress;
    if (locationData.lan) headers['lan'] = locationData.lan;
    if (locationData.latitude) headers['latitude'] = locationData.latitude;
    if (locationData.longitude) headers['longitude'] = locationData.longitude;

    const response = await fetch(`https://api.merkaru.com/v1/reasons?type=${type}`, {
      method: 'GET',
      headers: headers
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching reasons:', error);
    throw error;
  }
};


/**
 * Generate QR code
 * @param {Object} qrData - The data for QR code generation
 * @param {string} token - Bearer token for authorization
 * @returns {Promise<Object>} - API response
 */
export const generateQrCode = async (qrData, token) => {
  try {
    const bearerToken = token || getBearerTokenFromCookies();
    const response = await fetch('https://api.merkaru.com/v1/generateQrCode', {
      method: 'POST',
      headers: {
        'accept': '*/*',
        'accept-language': 'en-US,en;q=0.9',
        'authorization': `${bearerToken}`,
        'content-type': 'application/json',
        'platform': '3',
      },
      body: JSON.stringify(qrData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error generating QR code:', error);
    throw error;
  }
};

/**
 * Cancel a deal order
 * @param {Object} payload - The payload containing orderId and reason
 * @param {string} token - Bearer token for authorization
 * @returns {Promise<Object>} - API response
 */
export const cancelDealOrder = async (payload, token) => {
  try {
    const bearerToken = token || getBearerTokenFromCookies();
    const response = await fetch('https://api.merkaru.com/v1/orders/cancelDeal', {
      method: 'POST',
      headers: {
        'accept': '*/*',
        'accept-language': 'en-US,en;q=0.9',
        'authorization': `${bearerToken}`,
        'content-type': 'application/json',
        'platform': '3',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error cancelling deal order:', error);
    throw error;
  }
};

/**
 * Cancel an offer
 * @param {string} offerId - The offer ID to cancel (mandatory)
 * @param {string} reason - The reason for cancellation (mandatory)
 * @param {string} token - Bearer token for authorization
 * @param {boolean} isExchangeOffer - Whether the offer is an exchange offer
 * @param {string} exchangeAssetId - The exchange asset ID to cancel
 * @returns {Promise<Object>} - API response
 */
export const cancelOffer = async (offerId, reason, token) => {
  // Validate mandatory parameters
  if (!offerId) {
    throw new Error('offerId is mandatory');  
  }
  if (!reason) {
    throw new Error('reason is mandatory');
  }



  try {
    const bearerToken = token || getBearerTokenFromCookies();
    const payload = {
      offerId: offerId,
      reason: reason,
      trigger: 2
    };

   

  


    const response = await fetch('https://api.merkaru.com/v1/offer', {
      method: 'PUT',
      headers: {
        'accept': '*/*',
        'accept-language': 'en-US,en;q=0.9',
        'authorization': `${bearerToken}`,
        'content-type': 'application/json',
        'platform': '3',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error cancelling offer:', error);
    throw error;
  }
};

/**
 * Edit an offer
 * @param {string} offerId - The offer ID to cancel (mandatory)
 * @param {string} amount - The amount for the offer (mandatory)
 * @param {string} currency - The currency for the amount offered (mandatory)
 * @param {string} token - Bearer token for authorization
 * @returns {Promise<Object>} - API response
 */
export const editOffer = async (offerId, amount, currency, token) => {
  // Validate mandatory parameters
  if (!offerId) {
    throw new Error('offerId is mandatory');
  }
  if (!amount) {
    throw new Error('amount is mandatory');
  }
  if (!currency) {
    throw new Error('currency is mandatory');
  }

  try {
    const bearerToken = token || getBearerTokenFromCookies();
    const payload = {
      offerId: offerId,
      amount: amount,
      currency: currency,
      trigger: 1
    };

    const response = await fetch('https://api.merkaru.com/v1/offer', {
      method: 'PUT',
      headers: {
        'accept': '*/*',
        'accept-language': 'en-US,en;q=0.9',
        'authorization': `${bearerToken}`,
        'content-type': 'application/json',
        'platform': '3',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      // Get the error response data
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        // If we can't parse the error response, use the default message
        console.warn('Could not parse error response:', parseError);
      }

      // Create error object with status and message
      const error = new Error(errorMessage);
      error.status = response.status;
      error.response = response;
      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error editing offer:', error);
    throw error;
  }
};

/**
 * Fetch rating parameters from the API
 * @param {string} token - Bearer token for authorization
 * @returns {Promise<Object>} - API response
 */
export const fetchRatingParameters = async (token) => {
  try {
    const bearerToken = token || getBearerTokenFromCookies();
    const response = await fetch('https://api.merkaru.com/v1/ratingParameter', {
      method: 'GET',
      headers: {
        'accept': '*/*',
        'accept-language': 'en-US,en;q=0.9',
        'authorization': `${bearerToken}`,
        'content-type': 'application/json',
        'platform': '3',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching rating parameters:', error);
    throw error;
  }
};

/**
 * Submit rating review
 * @param {Object} payload - The payload containing orderId, linkedWith, ratings, and review
 * @param {string} token - Bearer token for authorization
 * @returns {Promise<Object>} - API response
 */
export const submitRating = async (payload, token) => {
  try {
    const bearerToken = token || getBearerTokenFromCookies();
    const response = await fetch('https://api.merkaru.com/v1/reviewRating', {
      method: 'POST',
      headers: {
        'accept': '*/*',
        'accept-language': 'en-US,en;q=0.9',
        'authorization': `${bearerToken}`,
        'content-type': 'application/json',
        'platform': '3',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error submitting rating:', error);
    throw error;
  }
};

/**
 * Validate QR code
 * @param {string} qrCode - The QR code to validate
 * @param {string} orderId - The order ID associated with the QR code
 * @returns {Promise<Object>} - API response
 */
export const validateQr = async (qrCode, orderId) => {
  try {
    const bearerToken = getBearerTokenFromCookies();
    const payload = {
      qrCode: qrCode,
      orderId: orderId
    };

    const response = await fetch('https://api.merkaru.com/v1/qrCode', {
      method: 'POST',
      headers: {
        'accept': '*/*',
        'accept-language': 'en-US,en;q=0.9',
        'authorization': `${bearerToken}`,
        'content-type': 'application/json',
        'platform': '3',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error validating QR code:', error);
    throw error;
  }
};


/**
 * Counter an offer
 * @param {string} offerId - The offer ID to cancel (mandatory)
 * @param {string} amount - The amount for the offer (mandatory)
 * @param {string} currency - The currency for the amount offered (mandatory)
 * @param {string} token - Bearer token for authorization
 * @returns {Promise<Object>} - API response
 */
export const counterOffer = async (offerId, amount, currency, token) => {
  // Validate mandatory parameters
  if (!offerId) {
    throw new Error('offerId is mandatory');
  }
  if (!amount) {
    throw new Error('amount is mandatory');
  }
  if (!currency) {
    throw new Error('currency is mandatory');
  }

  try {
    const bearerToken = token || getBearerTokenFromCookies();
    const payload = {
      offerId: offerId,
      amount: amount,
      currency: currency,
      offerType: 3,
      isExchange: false,
    };

    const response = await fetch('https://api.merkaru.com/v1/offer', {
      method: 'PATCH',
      headers: {
        'accept': '*/*',
        'accept-language': 'en-US,en;q=0.9',
        'authorization': `${bearerToken}`,
        'content-type': 'application/json',
        'platform': '3',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      // Get the error response data
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        // If we can't parse the error response, use the default message
        console.warn('Could not parse error response:', parseError);
      }

      // Create error object with status and message
      const error = new Error(errorMessage);
      error.status = response.status;
      error.response = response;
      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error countering offer:', error);
    throw error;
  }
};


/**
 * Accept an offer
 * @param {string} offerId - The offer ID to cancel (mandatory)
 * @param {string} amount - The amount for the offer (mandatory)
 * @param {string} currency - The currency for the amount offered (mandatory)
 * @param {string} token - Bearer token for authorization
 * @returns {Promise<Object>} - API response
 */
export const acceptOffer = async (offerId, amount, currency, token, isExchangeOffer, exchangeAssetId) => {
  // Validate mandatory parameters
  if (!offerId) {
    throw new Error('offerId is mandatory');
  }
  if (!amount) {
    throw new Error('amount is mandatory');
  }
  if (!currency) {
    throw new Error('currency is mandatory');
  }

  try {
    const bearerToken = token || getBearerTokenFromCookies();
    const payload = {
      offerId: offerId,
      amount: amount,
      currency: currency,
      offerType: 1,
      isExchange: isExchangeOffer,
      exchangeAssetId: exchangeAssetId
    };

    const response = await fetch('https://api.merkaru.com/v1/offer', {
      method: 'PATCH',
      headers: {
        'accept': '*/*',
        'accept-language': 'en-US,en;q=0.9',
        'authorization': `${bearerToken}`,
        'content-type': 'application/json',
        'platform': '3',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      // Get the error response data
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        // If we can't parse the error response, use the default message
        console.warn('Could not parse error response:', parseError);
      }

      // Create error object with status and message
      const error = new Error(errorMessage);
      error.status = response.status;
      error.response = response;
      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error accepting offer:', error);
    throw error;
  }
};
